import { z } from "zod"
import { Converter, ConverterDeps, FileInfo, narrowDepsOrThrow } from "./converter-interface"
import { ProgressUpdate } from "../../../../shared/progress"
import { IfcProgressStage } from "../shared/iframe-api"
import axios from "axios"
import { resolveTemplateStringAndLogErrors } from "../../../../helpers/resolve-template-string"
import { v4 as uuidv4 } from "uuid"
import { queryClient } from "../../../../queryClient"
import { DEFAULT_BYTES_PER_REQUEST } from "../shared/ifc-to-ld-config"
import { toAbsoluteUrl, getEncodedPathName } from "../urls"

const DEFAULT_GRAPH_URI_TEMPLATE = "https://wistor.nl/ifc/v4/graph/{{generated_uuid}}"

const reserveJobResponseSchema = z.object({
  jobId: z.string(),
  graphUri: z.string()
})

export class FragToLdConverter implements Converter {
  static queryKey = "frags-without-ld" as const
  target = "ld" as const
  deps: { current: ConverterDeps }

  constructor(deps: { current: ConverterDeps }) {
    this.deps = deps
  }

  get queryKey() {
    return FragToLdConverter.queryKey
  }

  async findConversionCandidates(ifcOrFragUrls: string[]): Promise<FileInfo[]>  {
    const deps = narrowDepsOrThrow(this.deps.current)

    const result = await deps.sparqlManager.fetch(`
        # DEBT: Due to a bug in SparqlManager, a timestamp is necessary to properly invalidate it
        #${Date.now()}
        #nocache

        PREFIX uaf: <http://wistor.nl/upload/admin/uploadanyfile#>
        PREFIX ifcv4: <https://wistor.nl/ifc/v4#>
  
        SELECT ?frag ?fragDownloadUrl ?fragFilename WHERE {
            { # find Fragment file when IFC is loaded
              SELECT distinct ?frag ?fragDownloadUrl ?fragFilename  {
                VALUES (?ifcDownloadUrl) {
                  ${ifcOrFragUrls.map(u => `("${u}")`).join("\n")}
                }
                ?ifc a uaf:UploadedFile ;
                    uaf:filename ?ifcRelativeUrl ;
                    uaf:format "ifc" ;
                    uaf:name ?ifcFilename .
                FILTER(STRENDS(?ifcDownloadUrl, ?ifcRelativeUrl))
                
                ?frag uaf:format "frag" ;
                      uaf:belongsTo ?ifc ;
                      uaf:filename ?fragRelativeUrl ;
                      uaf:name ?fragFilename .
                BIND(CONCAT("${deps.appCtx.urlDownload}/", ?fragRelativeUrl) as ?fragDownloadUrl)
              }
            } UNION {
              # find Fragment file when Fragment is loaded
              VALUES (?fragDownloadUrl) {
                ${ifcOrFragUrls.map(u => `("${u}")`).join("\n")}
              }
              
              ?frag uaf:format "frag" ;
                    uaf:belongsTo ?ifc ;
                    uaf:filename ?fragRelativeUrl ;
                    uaf:name ?fragFilename .
              FILTER(STRENDS(?fragDownloadUrl, ?fragRelativeUrl))
            }

            FILTER NOT EXISTS {
              GRAPH ?graph {
                ?frag ifcv4:relatedGraph ?graph .
              }
            }
        }
      `)

    if (result.isError) throw new Error("SparqlManager returned error")

    return result.result.results.bindings.map(b => {
      return {
        type: "frag",
        uri: b.frag.value,
        downloadUrl: toAbsoluteUrl(getEncodedPathName(b.fragDownloadUrl.value)),
        filename: b.fragFilename.value
      } satisfies FileInfo
    })
  }

  static async refetch() {
    await queryClient.refetchQueries({ queryKey: [ this.queryKey ] })
  }

  static async invalidate() {
    await queryClient.invalidateQueries({ queryKey: [ this.queryKey ] })
  }

  private get graphUploadApi() {
    return axios.create({
      baseURL: narrowDepsOrThrow(this.deps.current).appCtx.makeUrl(ctx => `${ctx.origin}/servlets/cgi/api/${ctx.repoName}/graph-upload/chunked-jobs`),
      withCredentials: true,
    })
  }

  async convert(
    frag: FileInfo,
    progress: (input: ProgressUpdate<IfcProgressStage>) => void,
  ): Promise<void> {
    const deps = narrowDepsOrThrow(this.deps.current)
    if (frag.type !== "frag") throw new Error(`Can only convert frag to LD, got: ${JSON.stringify(frag)}`)

    const ifcToLdConfig = deps.widgetConfig.ifcToLdConfig ?? {}
    const minimumBytesPerRequest = ifcToLdConfig.streaming?.minimumBytesPerRequest ?? DEFAULT_BYTES_PER_REQUEST

    const graphUri = resolveTemplateStringAndLogErrors(
      deps.widgetConfig.ifcLdGraph || DEFAULT_GRAPH_URI_TEMPLATE,
      { ...deps.appVars, generated_uuid: uuidv4() },
    )

    const stream = await deps.iframeApi.convertFragToLd({
      fragUri: frag.uri,
      fragUrl: frag.downloadUrl,
      targetGraphUri: graphUri,
      ifcToLdConfig
    })

    if (!stream) throw new Error("No conversion stream returned from iframe")

    console.log("Starting IFC to LD streaming")

    try {
      const jobId = await this.reserveUploadJob(graphUri)
      try {
        await this.pumpChunksToJob(stream, jobId, minimumBytesPerRequest)
      } finally {
        await this.releaseUploadJob(jobId)
      }
    } catch (e) {
      // Only emit error in progress, more detailed progress is emitted from iframe
      progress({
        item: frag.downloadUrl,
        stage: "stream-ifc-to-ld",
        status: "error",
        error: e instanceof Error ? e : new Error(String(e)),
      })
      throw e
    }
  }

  private async reserveUploadJob(graphUri: string): Promise<string> {
    const response = await this.graphUploadApi.post(
      "/", null, { params: { graphUri } },
    )
    const jobResponse = reserveJobResponseSchema.parse(response.data)
    const jobId = jobResponse.jobId
    if (!jobId) throw new Error("Expected a jobId from job reservation")
    return jobId
  }

  private async releaseUploadJob(jobId: string): Promise<void> {
    try {
      await this.graphUploadApi.delete(`/${jobId}`)
    } catch (e) {
      console.error("Failed to delete job", e)
    }
  }

  private async pumpChunksToJob(
    stream: ReadableStream<string>,
    jobId: string,
    minimumBytesPerRequest: number
  ): Promise<void> {
    let buffer = ""

    const flush = async () => {
      if (!buffer.length) return
      const body = buffer
      buffer = ""
      await this.graphUploadApi.post(`/${jobId}`, body, {
        headers: { "Content-Type": "application/n-triples" }
      })
    }

    const reader = stream.getReader()
    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += value.endsWith("\n") ? value : value + "\n"
        if (buffer.length >= minimumBytesPerRequest) await flush()
      }
      await flush()
    } finally {
      reader.releaseLock()
    }
  }
}
